import React, { useMemo } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { chartColors } from "../../styles/palette";
import { BrandDistribution } from "../../api/dashboard";

type Props = {
  data: BrandDistribution[];
  emptyLabel?: string;
  otherLabel?: string;
  maxSlices?: number;
};

/**
 * Camembert (pie) view of the fleet's brand mix. Long tails beyond
 * `maxSlices` are folded into a single "Others" slice so the chart and
 * legend stay readable regardless of fleet diversity.
 */
export const BrandPie: React.FC<Props> = ({
  data,
  emptyLabel = "No vehicles yet",
  otherLabel = "Others",
  maxSlices = 6,
}) => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const colors = chartColors[mode];

  const total = useMemo(() => data.reduce((s, d) => s + d.count, 0), [data]);

  const chartData = useMemo(() => {
    const sorted = [...data].sort((a, b) => b.count - a.count);
    const head = sorted.slice(0, maxSlices);
    const tail = sorted.slice(maxSlices);
    const rest = tail.reduce((s, d) => s + d.count, 0);
    const slices = head.map((d, i) => ({
      name: d.brand,
      value: d.count,
      color: colors[i % colors.length],
    }));
    if (rest > 0) {
      slices.push({ name: otherLabel, value: rest, color: theme.palette.text.disabled });
    }
    return slices;
  }, [data, maxSlices, colors, otherLabel, theme.palette.text.disabled]);

  if (!data.length) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", height: "100%", minHeight: 160 }}>
        <Typography sx={{ color: "text.disabled", fontSize: 13 }}>{emptyLabel}</Typography>
      </Box>
    );
  }

  return (
    <Stack direction={{ xs: "column", sm: "row" }} alignItems="center" spacing={2} sx={{ height: "100%" }}>
      <Box sx={{ position: "relative", width: 200, height: 200, flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={92}
              paddingAngle={total > 0 ? 2 : 0}
              cornerRadius={4}
              stroke="none"
            >
              {chartData.map((entry, i) => (
                <Cell key={i} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                background: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 10,
                fontSize: 12,
                color: theme.palette.text.primary,
              }}
              itemStyle={{ color: theme.palette.text.primary }}
            />
          </PieChart>
        </ResponsiveContainer>
      </Box>

      <Stack spacing={1.25} sx={{ flex: 1, width: "100%", maxHeight: 200, overflowY: "auto" }}>
        {chartData.map((entry) => {
          const pct = total > 0 ? Math.round((entry.value / total) * 100) : 0;
          return (
            <Stack key={entry.name} direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ width: 10, height: 10, borderRadius: "3px", bgcolor: entry.color, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{entry.name}</Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="baseline">
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary" }}>{entry.value}</Typography>
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>({pct}%)</Typography>
              </Stack>
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
};
